import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import {
  cloneElement,
  type FC,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form@latest';
import { useSelector } from 'react-redux';

import {
  ApprovalTypes,
  approvalTypeToTextMap,
  type ApproversFormData,
  isActive,
  isPositiveInteger,
  lessThanOrEqual,
  mapApproversToFormData,
  mapFormDataToApprovers,
} from '@/components/Glossary/Sections/Approvals/TemplateForm/Approvers.utils';
import SortableItem from '@/components/Draggable/SortableItem';
import SortableList from '@/components/Draggable/SortableList';
import Chip from '@/components/UI/Chip/Chip';
import Radio from '@/components/UI/FormElements/Radio';
import SearchSelect from '@/components/UI/SearchSelect';
import UserCard from '@/components/UserCard';
import { missingNameUseEmail } from '@/helpers';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { Approval } from '@/model/Approval.model';
import type { ID } from '@/model/Common.model';
import type { Team } from '@/model/Team.model';
import type { MainUser } from '@/model/User.model';

interface ApproversProps {
  ActionButtons: ReactElement<{ onOk?: () => void; canOk?: boolean; tooltip?: string }>;
  onSubmit: (assignees: unknown) => void;
  approval: Approval;
}

const Approvers: FC<ApproversProps> = ({ ActionButtons, onSubmit, approval }) => {
  const [approvalUsers, setApprovalUsers] = useState<Array<MainUser>>([]);

  const defaultValues = mapApproversToFormData(approval.approvers);
  const formMethods = useForm<ApproversFormData>({
    defaultValues,
  });
  const {
    handleSubmit,
    formState: { errors },
    control,
    register,
    getValues,
    setValue,
  } = formMethods;
  const { team } = useSelector(({ team: { team } }: { team: { team: Team } }) => ({
    team,
  }));
  const approvers = useWatch({ control, name: 'approvers' });
  const approvalType = useWatch({ control, name: 'type' });

  const users =
    team?.users?.reduce<Array<MainUser>>((acc, user) => {
      if (isActive(user) || (approval.approvers?.ids.includes(user.id) && approvers?.includes(user.id))) {
        acc.push(missingNameUseEmail(user));
      }
      return acc;
    }, []) ?? [];

  const sections = useMemo(
    () => [
      {
        items: users,
      },
    ],
    [team, users]
  );

  const usersLookup = users?.reduce<Record<ID, MainUser>>((acc, user) => {
    acc[Number(user.id)] = user;
    return acc;
  }, {});

  const handleReorder = useCallback((sourceIndex: number, destinationIndex: number) => {
    setApprovalUsers((prev) => {
      const reordered = [...prev];
      const [moved] = reordered.splice(sourceIndex, 1);
      reordered.splice(destinationIndex, 0, moved);
      return reordered;
    });
  }, []);

  useEffect(() => {
    if (_notNil(team?.users)) {
      const users =
        approvers?.reduce<Array<MainUser>>((acc, approverId) => {
          const user = team.users.find(({ id }) => id === approverId);
          if (_notNil(user)) {
            acc.push(user);
          }
          return acc;
        }, []) ?? [];
      setApprovalUsers(users);
    }
  }, [team?.users, approvers]);

  useEffect(() => {
    if (approvalType !== ApprovalTypes.min_required) {
      const minNumberApprovers = getValues('minNumberApprovers');
      if (_notNil(minNumberApprovers)) {
        setValue('minNumberApprovers', undefined);
      }
    }
  }, [approvalType]);

  // When the user drops back down to ≤1 approver the Customization radios are
  // hidden, so reset the stored type to the default `unordered` so the radios
  // come back in a known state once a 2nd approver is added again. (The legacy
  // v6 implementation paired this with manual `register/unregister('type')` —
  // unnecessary in v7 because the Controller registers/unregisters itself.)
  useEffect(() => {
    if (_isNil(approvers) || approvers.length <= 1) {
      setValue('type', ApprovalTypes.unordered);
    }
  }, [approvers]);

  const handleOrderingOnSubmit = (formData: ApproversFormData) => {
    onSubmit(mapFormDataToApprovers(formData, approvalUsers));
  };

  const ordered = approvalType === ApprovalTypes.ordered;

  const canSubmit =
    _notNil(approvers) &&
    _isNotEmpty(approvers) &&
    approvers.every((userId: ID) => {
      const user = usersLookup[userId];
      return _notNil(user) && isActive(user);
    });

  return (
    <FormProvider {...formMethods}>
      <form data-test-component="Approvers" data-test-element="form">
        <div className="flex flex-row items-start">
          <div className="mb3 mr4" style={{ width: 350 }} data-test-element="approvers-search-select-container">
            <Controller
              name="approvers"
              control={control}
              rules={{
                required: true,
                validate: (data: Array<ID>) => {
                  if (!_isNotEmpty(data)) {
                    return 'You must select at least one reviewer';
                  }
                  return true;
                },
              }}
              render={({ field: { value, onChange } }) => (
                <div className="ui-card">
                  <SearchSelect
                    className={cn({ search_select__error: _notNil(errors.approvers) })}
                    sections={sections}
                    selected={value}
                    setSelected={onChange}
                    sort
                    sortAccessor={'name'}
                  />
                </div>
              )}
            />
            <ErrorMessage
              errors={errors}
              name="approvers"
              render={({ message }) => <p className="f6 red db lh-copy pt1">{message}</p>}
            />
          </div>

          <div className="flex flex-column" style={{ width: 450 }} data-test-element="approvers-detail-container">
            {_notNil(approvers) && approvers.length > 1 && (
              <>
                <h2 className="f5 near-black mb1">Customization</h2>
                <div className="f6 dark-gray mb3">How would you like to order this review?</div>
                <Controller
                  name="type"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <div className="mb3" data-test-element="approval-type-container">
                      {Object.values(ApprovalTypes).map((approvalType, i) => (
                        <Radio
                          key={approvalType}
                          index={i}
                          checked={value === approvalType}
                          onChange={() => onChange(approvalType)}
                          id={`approval-type-${approvalType}`}
                          name={`approval-type-${approvalType}`}
                          label={approvalTypeToTextMap[approvalType]}
                          value={approvalType}
                          className="mb2"
                        />
                      ))}
                      {value === ApprovalTypes.min_required && (
                        <div style={{ marginLeft: 30 }}>
                          <input
                            type="text"
                            data-test-element="min-approvers-input"
                            className={cn({ input__error: _notNil(errors.minNumberApprovers) })}
                            {...register('minNumberApprovers', {
                              required: 'A minimum number is required',
                              validate: {
                                isPositiveInteger,
                                lessThanEqualNumberApprovers: lessThanOrEqual(
                                  approvers,
                                  'Enter a number lower than the amount of reviewers'
                                ),
                              },
                            })}
                            style={{ marginBottom: 0, width: 50 }}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="minNumberApprovers"
                            render={({ message }) => <p className="f6 red db pt2">{message}</p>}
                          />
                        </div>
                      )}
                    </div>
                  )}
                />
              </>
            )}

            {_isNotEmpty(approvalUsers) && (
              <div className="mb3">
                <UserCardContainer>
                  <SortableList
                    droppableId="approvers-list"
                    onReorder={handleReorder}
                    disabled={!(ordered && approvalUsers.length > 1)}
                  >
                    {approvalUsers.map((user, index) => (
                      <div key={user.id}>
                        {index !== 0 && <div className="bt b--moon-gray"></div>}
                        {ordered && approvalUsers.length > 1 ? (
                          <SortableItem
                            id={String(user.id)}
                            index={index}
                            showDraggableIcon
                            className="flex flex-row justify-between items-center w-100"
                          >
                            <div className="flex flex-row items-center">
                              <Chip value={index + 1} />
                              <UserCard
                                name={user.name}
                                email={user.email}
                                inactive={user.pivot.status === 'inactive'}
                              />
                            </div>
                          </SortableItem>
                        ) : (
                          <UserCard name={user.name} email={user.email} inactive={user.pivot.status === 'inactive'} />
                        )}
                      </div>
                    ))}
                  </SortableList>
                </UserCardContainer>
              </div>
            )}
          </div>
        </div>

        {cloneElement(ActionButtons, {
          onOk: handleSubmit(handleOrderingOnSubmit),
          tooltip: canSubmit ? '' : 'There are inactive users selected',
          canOk: canSubmit,
        })}
      </form>
    </FormProvider>
  );
};

export default Approvers;

const UserCardContainer: FC<{ children: ReactNode }> = ({ children }) => {
  return <div className="ui-card user-card-container ph3">{children}</div>;
};
